/* @flow */

import * as React from 'react';
import {useSelector} from 'react-redux';

import {Products} from 'nutshell-core/products';
import type {ProductId, Market} from 'nutshell-core/types';

import {ReduxSelectProducts} from '../../../../../ui/core/select/redux-select-products';

import {CancelButton} from '../button';
import {TextField} from '../textfield';

import './product-picker.module.css';

type Props = {|
    input: {
        value: ?Object,
        onChange: (productMap: Object) => any,
    },
    selectedOptions: Object[],
    onRemove: () => any,
    currentMarket: ?Market,
|};

export function ReduxFormProductPicker(props: Props) {
    const {currentMarket, input} = props;
    const products = useSelector((state) => Products.getAll(state));

    const [productId, setProductId] = React.useState<?ProductId>(
        (input.value && input.value.links && input.value.links.product) || null
    );
    const [quantity, setQuantity] = React.useState<string>(
        (input.value && input.value.quantity) || ''
    );
    const [price, setPrice] = React.useState<string>((input.value && input.value.price) || '');

    const getCurrencySymbol = () => {
        return currentMarket ? currentMarket.currencySymbol : '';
    };

    // if market changes, update the price to reflect the new market
    React.useEffect(() => {
        if (currentMarket) {
            const selectedProduct = products.find((item) => item.id === productId);
            const marketPrice = selectedProduct && selectedProduct.prices[currentMarket.id];
            const newPrice = marketPrice || {amount: 0};

            handleChangePrice(newPrice.amount.toString());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentMarket]);

    React.useEffect(() => {
        if (productId) {
            handleSave();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId, quantity, price]);

    const handleClearProduct = () => {
        setProductId(null);
        setQuantity('');
        setPrice('');

        if (input.value) {
            props.onRemove();
        }
    };

    const handleKeyDownProductInputs = (event) => {
        // Capture the ENTER key to confirm the product and not submit the form.
        if (event.which === 13) {
            event.preventDefault();
            handleSave();
        }

        // Capture the ESC key to cancel the product picker.
        if (event.which === 27) {
            event.preventDefault();
            handleClearProduct();
        }
    };

    const handleSelectProduct = (value) => {
        const existingProductMap = typeof input.value === 'object' ? input.value : undefined;

        const selectedProduct = products.find((item) => item.id === value.value);

        let productPrice;
        if (existingProductMap) {
            productPrice = existingProductMap.price.amount;
        }

        if (selectedProduct && currentMarket) {
            const marketPrice = selectedProduct.prices[currentMarket.id];
            productPrice = marketPrice ? marketPrice.amount : '0';
        } else if (selectedProduct) {
            productPrice = '0';
        }

        setProductId(value.value);
        setPrice(productPrice || '0');
        setQuantity(existingProductMap ? existingProductMap.quantity : '1');
    };

    const handleChangeQuantity = (newQuantity: string) => {
        setQuantity(newQuantity);
    };

    const handleBlurQuantity = () => {
        if (quantity === '0') {
            handleClearProduct();
        } else if (quantity === '') {
            setQuantity('1');
        }
    };

    const handleChangePrice = (newPrice: string) => {
        setPrice(newPrice);
    };

    const handleBlurPrice = () => {
        const formattedPrice = Number(price).toLocaleString(undefined, {
            minimumFractionDigits: 2,
        });

        setPrice(formattedPrice);
    };

    const handleSave = () => {
        const existingProductMap = typeof input.value === 'object' ? input.value : undefined;

        const amount =
            price && typeof price === 'string' ? parseFloat(price.replace(/,/g, '')) : 0.0;
        const productPrice = {
            amount,
            formatted: `${getCurrencySymbol()}${price}`,
        };

        const productQuantity = Math.round(Number(quantity || '1'));

        if (existingProductMap) {
            const updatedProductMap = {
                ...existingProductMap,
                quantity: productQuantity,
                price: productPrice,
            };

            input.onChange(updatedProductMap);
        } else {
            const product = products.find((item) => item.id === productId);

            if (!product) return;

            input.onChange({
                type: 'productMaps',
                name: product.name,
                quantity: quantity,
                price: price,
                links: {
                    product: productId,
                },
            });
        }
    };

    // filter out already selected products from the list of available products
    const excludeProductIds = props.selectedOptions
        .filter((field) => field && field.links && field.links.product)
        .map((field) => field.links.product);

    return (
        <div styleName='container'>
            <div styleName='select-container'>
                <ReduxSelectProducts
                    selectedId={productId}
                    onChange={handleSelectProduct}
                    currentMarket={currentMarket}
                    excludeProductIds={excludeProductIds}
                    placeholder='Select a product…'
                    defaultMenuIsOpen={false}
                    maxMenuHeight={200}
                />
            </div>
            <label styleName='product-picker-label'>
                Qty:
                <TextField
                    name='quantity'
                    disabled={!productId}
                    min='0'
                    type='number'
                    value={quantity}
                    onChange={handleChangeQuantity}
                    onBlur={handleBlurQuantity}
                    onKeyDown={handleKeyDownProductInputs}
                    selectOnFocus={true}
                />
            </label>
            <label styleName='product-picker-label--price'>
                Price:
                <TextField
                    disabled={!productId}
                    value={price}
                    onChange={handleChangePrice}
                    onBlur={handleBlurPrice}
                    onKeyDown={handleKeyDownProductInputs}
                    selectOnFocus={true}
                />
            </label>
            <div styleName='cancel-button'>
                {productId && <CancelButton onClick={handleClearProduct} />}
            </div>
        </div>
    );
}
